import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LoginSchema, type LoginInput } from '@app/shared';
import axios from 'axios';
import { useAuthStore } from '../features/auth/store';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const user = useAuthStore((s) => s.user);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirect = new URLSearchParams(location.search).get('redirect') || '/dashboard';

  // 已登入直接跳走。
  useEffect(() => {
    if (user) navigate(redirect, { replace: true });
  }, [user, redirect, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '', remember: false },
  });

  const onSubmit = async (values: LoginInput): Promise<void> => {
    setServerError(null);
    setSubmitting(true);
    try {
      await login(values);
      navigate(redirect, { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 401) setServerError('帳號或密碼不正確');
        else if (status === 429) setServerError('嘗試次數過多，請稍後再試');
        else setServerError('登入失敗，請稍後再試');
      } else {
        setServerError('登入失敗，請稍後再試');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="max-w-sm mx-auto bg-white shadow-sm rounded-md p-6 mt-4">
      <h1 className="text-2xl font-semibold mb-4">登入</h1>
      {serverError ? (
        <div role="alert" className="mb-3 text-sm text-red-600">
          {serverError}
        </div>
      ) : null}
      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3">
        <label className="grid gap-1">
          <span className="text-sm text-slate-700">Email</span>
          <input
            type="email"
            autoComplete="email"
            {...register('email')}
            className="border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {errors.email ? (
            <span role="alert" className="text-sm text-red-600">
              {errors.email.message}
            </span>
          ) : null}
        </label>
        <label className="grid gap-1">
          <span className="text-sm text-slate-700">密碼</span>
          <input
            type="password"
            autoComplete="current-password"
            {...register('password')}
            className="border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {errors.password ? (
            <span role="alert" className="text-sm text-red-600">
              {errors.password.message}
            </span>
          ) : null}
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" {...register('remember')} /> 記住我（30 天）
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="bg-blue-600 text-white rounded px-3 py-2 hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? '登入中…' : '登入'}
        </button>
        <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline text-center">
          忘記密碼？
        </Link>
      </form>
    </section>
  );
}
